function binary(bytes) {
  let result = ''
  for (const byte of new Uint8Array(bytes)) result += String.fromCharCode(byte)
  return result
}

function decode(value) {
  if (typeof value !== 'string') throw new Error('Invalid ciphertext field')
  return Uint8Array.from(atob(value), c => c.charCodeAt(0))
}

function params(envelope, status) {
  const iv = decode(envelope.iv), tag = decode(envelope.tag), ciphertext = decode(envelope.ciphertext)
  if (iv.length !== 12 || tag.length !== 16) throw new Error('Invalid GCM parameters')
  const aad = new TextEncoder().encode(`niuhui-response-v1\n${envelope.requestId}\n${status}`)
  return { iv, tag, ciphertext, aad }
}

export async function createResponseCryptoSession(cryptoApi, loadForge = () => import(/* webpackChunkName: "response-crypto-http" */ 'node-forge')) {
  if (!cryptoApi || typeof cryptoApi.getRandomValues !== 'function') {
    const error = new Error('浏览器缺少安全随机数功能，请更新浏览器后重试。')
    error.code = 'RESPONSE_ENCRYPTION_UNAVAILABLE'
    throw error
  }
  if (cryptoApi.subtle) {
    const pair = await cryptoApi.subtle.generateKey({ name: 'RSA-OAEP', modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-256' }, false, ['encrypt', 'decrypt'])
    return {
      publicKey: btoa(binary(await cryptoApi.subtle.exportKey('spki', pair.publicKey))),
      async decrypt(envelope, status) {
        const rawKey = new Uint8Array(await cryptoApi.subtle.decrypt({ name: 'RSA-OAEP' }, pair.privateKey, decode(envelope.encryptedKey)))
        let key
        try {
          if (rawKey.length !== 32) throw new Error('Invalid AES key size')
          key = await cryptoApi.subtle.importKey('raw', rawKey, 'AES-GCM', false, ['decrypt'])
        } finally { rawKey.fill(0) }
        const { iv, tag, ciphertext, aad } = params(envelope, status)
        const combined = new Uint8Array(ciphertext.length + tag.length)
        combined.set(ciphertext); combined.set(tag, ciphertext.length)
        return new Uint8Array(await cryptoApi.subtle.decrypt({ name: 'AES-GCM', iv, tagLength: 128, additionalData: aad }, key, combined))
      }
    }
  }

  // HTTP LAN pages do not expose subtle. Retain the protocol and use native secure randomness.
  const module = await loadForge()
  const forge = module.default || module
  const randomBytes = count => {
    const bytes = new Uint8Array(count)
    for (let offset = 0; offset < count; offset += 65536) cryptoApi.getRandomValues(bytes.subarray(offset, offset + 65536))
    return binary(bytes)
  }
  const prng = { getBytesSync: randomBytes, getBytes: (count, callback) => {
    try { callback(null, randomBytes(count)) } catch (error) { callback(error) }
  } }
  const pair = await new Promise((resolve, reject) => forge.pki.rsa.generateKeyPair(
    { bits: 2048, e: 65537, workers: 0, prng }, (error, keys) => error ? reject(error) : resolve(keys)))
  return {
    publicKey: forge.util.encode64(forge.asn1.toDer(forge.pki.publicKeyToAsn1(pair.publicKey)).getBytes()),
    async decrypt(envelope, status) {
      const { iv, tag, ciphertext, aad } = params(envelope, status)
      const rawKey = pair.privateKey.decrypt(atob(envelope.encryptedKey), 'RSA-OAEP', {
        md: forge.md.sha256.create(), mgf1: { md: forge.md.sha256.create() }
      })
      if (rawKey.length !== 32) throw new Error('Invalid AES key size')
      const decipher = forge.cipher.createDecipher('AES-GCM', rawKey)
      decipher.start({ iv: binary(iv), tagLength: 128, tag: forge.util.createBuffer(binary(tag)), additionalData: binary(aad) })
      decipher.update(forge.util.createBuffer(binary(ciphertext)))
      if (!decipher.finish()) throw new Error('Ciphertext authentication failed')
      return Uint8Array.from(decipher.output.getBytes(), c => c.charCodeAt(0))
    }
  }
}
